import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Cpu, Wifi, Plane, Diamond, Sparkles, Crown } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/* ----------------------------------------------------------------
   Distinctive credit card visualizations — each design is unique.
   Variants: graphite-gold | navy-stars | silver-metallic | modern-teal | emerald-luxury
----------------------------------------------------------------- */

const sizes = {
  sm: 'max-w-[280px] text-[13px]',
  md: 'max-w-[360px] text-sm',
  lg: 'max-w-[440px] text-base',
};

// When a card is rendered in **vertical** orientation we want it to live
// peacefully alongside horizontal cards in the same grid row. A naive
// `aspect-[1/1.586]` at the same width makes the portrait card ~2.5x taller
// than the landscape one and breaks row alignment.
//
// Strategy: cap the rendered height for vertical cards (height-driven) at the
// same height a horizontal card would have at the same `size`, and let the
// width auto-derive from the portrait aspect. That keeps the visual identity
// (a portrait card silhouette) while preventing wallet/admin grids from
// becoming visually broken.
//
// Heights below intentionally match each `size`'s horizontal height
// (width / 1.586):
//   sm: 280 / 1.586 ≈ 176  → bump slightly so portrait feels intentional
//   md: 360 / 1.586 ≈ 227  → cap at 260 so vertical is just a touch taller
//   lg: 440 / 1.586 ≈ 277  → cap at 320
const verticalHeights = {
  sm: 'h-[200px]',
  md: 'h-[260px]',
  lg: 'h-[320px]',
};

// Reusable inner pieces
const Chip = ({ tone = 'gold' }) => (
  <div
    className="h-9 w-12 rounded-md grid place-items-center relative overflow-hidden"
    style={{
      background:
        tone === 'gold'
          ? 'linear-gradient(180deg, #f5d98b 0%, #b88a2a 100%)'
          : 'linear-gradient(180deg, #e7e9ec 0%, #8b8e93 100%)',
    }}
  >
    <div className="absolute inset-0 opacity-30 bg-[linear-gradient(135deg,rgba(255,255,255,0.4),transparent_50%)]" />
    <Cpu className="relative h-4 w-4 text-black/35" />
  </div>
);

const StarMap = () => (
  <svg
    className="absolute inset-0 h-full w-full opacity-60"
    viewBox="0 0 360 220"
    fill="none"
    preserveAspectRatio="none"
  >
    {[
      [22, 28], [56, 18], [94, 42], [132, 22], [168, 56], [210, 28], [248, 42], [294, 18], [332, 36],
      [18, 72], [62, 88], [108, 78], [156, 96], [198, 76], [244, 92], [286, 70], [328, 96],
      [38, 124], [82, 138], [126, 122], [172, 144], [218, 132], [264, 152], [310, 134],
      [28, 178], [76, 168], [122, 192], [166, 176], [212, 196], [262, 184], [302, 200],
    ].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r={i % 5 === 0 ? 1.6 : 0.9} fill="#cfe1ff" opacity={i % 3 === 0 ? 0.95 : 0.55} />
    ))}
    {/* Constellation lines */}
    <g stroke="rgba(120,170,255,0.20)" strokeWidth="0.6">
      <line x1="22" y1="28" x2="56" y2="18" />
      <line x1="56" y1="18" x2="94" y2="42" />
      <line x1="94" y1="42" x2="132" y2="22" />
      <line x1="168" y1="56" x2="210" y2="28" />
      <line x1="210" y1="28" x2="248" y2="42" />
      <line x1="126" y1="122" x2="172" y2="144" />
      <line x1="172" y1="144" x2="218" y2="132" />
    </g>
  </svg>
);

const EmbossedTitle = ({ children, color = 'rgba(255,255,255,0.95)' }) => (
  <p
    className="font-display text-lg font-semibold leading-tight"
    style={{
      color,
      textShadow:
        '0 1px 0 rgba(0,0,0,0.45), 0 0 1px rgba(255,255,255,0.18)',
    }}
  >
    {children}
  </p>
);

// Variant 1: HDFC Infinia — brushed black metal with gold edges
const InfiniaDesign = ({ card }) => (
  <div className="absolute inset-0 metal-brushed-graphite gold-edge">
    <div className="absolute inset-0 bg-fine-grain opacity-50 mix-blend-overlay" />
    {/* Gold corner accent */}
    <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.30),transparent_60%)]" />
    {/* Subtle metallic lines */}
    <div className="absolute inset-0 opacity-30" style={{
      backgroundImage: 'linear-gradient(105deg, transparent 35%, rgba(255,220,140,0.10) 50%, transparent 65%)',
    }} />
  </div>
);
const InfiniaContent = ({ card }) => (
  <div className="relative flex h-full flex-col justify-between p-5 text-white">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-white/55">{card.bank}</p>
        <EmbossedTitle>{card.name}</EmbossedTitle>
      </div>
      <div className="flex items-center gap-2">
        <Crown className="h-3.5 w-3.5 text-gold" />
        <span className="text-[10px] uppercase tracking-[0.18em] text-gold border border-[rgba(212,175,55,0.40)] rounded-full px-2 py-0.5">METAL</span>
      </div>
    </div>

    <div className="flex items-center justify-between">
      <Chip tone="gold" />
      <Wifi className="h-4 w-4 text-white/60 rotate-90" />
    </div>

    <div>
      <p className="font-mono-num text-sm tracking-[0.18em] text-white/90">•••• •••• •••• {card.last4}</p>
      <div className="mt-2 flex items-end justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">Cardholder</p>
          <p className="text-sm text-white/95">Aarav Mehta</p>
        </div>
        <div className="font-display text-sm font-semibold text-white/95">{card.network}</div>
      </div>
    </div>
  </div>
);

// Variant 2: Axis Atlas — deep navy + star map + travel motif
const AtlasDesign = () => (
  <div className="absolute inset-0 metal-navy-stars">
    <StarMap />
    <div className="absolute inset-0 bg-fine-grain opacity-30 mix-blend-overlay" />
  </div>
);
const AtlasContent = ({ card }) => (
  <div className="relative flex h-full flex-col justify-between p-5 text-white">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#9ecaff]">{card.bank}</p>
        <p className="font-display text-lg font-semibold leading-tight">{card.name}</p>
      </div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#9ecaff] border border-[rgba(158,202,255,0.30)] rounded-full px-2 py-0.5">
        <Plane className="h-3 w-3" /> Travel
      </div>
    </div>

    <div className="flex items-center justify-between">
      <Chip tone="gold" />
      <Wifi className="h-4 w-4 text-white/60 rotate-90" />
    </div>

    <div>
      <p className="font-mono-num text-sm tracking-[0.18em] text-white/90">•••• •••• •••• {card.last4}</p>
      <div className="mt-2 flex items-end justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">Cardholder</p>
          <p className="text-sm text-white/95">Aarav Mehta</p>
        </div>
        <div className="font-display text-sm font-semibold text-white/95">{card.network}</div>
      </div>
    </div>
  </div>
);

// Variant 3: Amex Platinum Travel — silver metallic reflective
const SilverDesign = () => (
  <div className="absolute inset-0 metal-silver silver-edge">
    <div className="absolute inset-0 bg-fine-grain opacity-30 mix-blend-multiply" />
    {/* Diagonal shimmer */}
    <div
      className="absolute -inset-x-10 -top-10 h-32 rotate-[18deg]"
      style={{ background: 'linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.55) 50%, transparent 75%)' }}
    />
  </div>
);
const SilverContent = ({ card }) => (
  <div className="relative flex h-full flex-col justify-between p-5" style={{ color: '#1c1d22' }}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-black/55">{card.bank}</p>
        <p className="font-display text-lg font-semibold leading-tight" style={{
          color: '#0e0f12',
          textShadow: '0 1px 0 rgba(255,255,255,0.45)',
        }}>{card.name}</p>
      </div>
      <span className="text-[10px] uppercase tracking-[0.18em] text-black/70 border border-black/15 rounded-full px-2 py-0.5">Platinum</span>
    </div>

    <div className="flex items-center justify-between">
      <Chip tone="silver" />
      <Wifi className="h-4 w-4 text-black/55 rotate-90" />
    </div>

    <div>
      <p className="font-mono-num text-sm tracking-[0.18em] text-black/85">•••• •••• •••• {card.last4}</p>
      <div className="mt-2 flex items-end justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-black/50">Cardholder</p>
          <p className="text-sm text-black/90">Aarav Mehta</p>
        </div>
        <div className="font-display text-sm font-semibold text-black/90">AMEX</div>
      </div>
    </div>
  </div>
);

// Variant 4: SBI Cashback — modern teal/black with cashback motif
const ModernDesign = () => (
  <div className="absolute inset-0 metal-modern-teal">
    <div className="absolute inset-0 bg-fine-grain opacity-25 mix-blend-overlay" />
    {/* Decorative percent-mark */}
    <div className="absolute -bottom-12 -right-8 font-display text-[180px] leading-none font-bold text-white/[0.04]">%</div>
    {/* Accent stripe */}
    <div className="absolute top-0 left-0 right-0 h-1 bg-[linear-gradient(90deg,#0f766e,#22d3ee,#0f766e)] opacity-60" />
  </div>
);
const ModernContent = ({ card }) => (
  <div className="relative flex h-full flex-col justify-between p-5 text-white">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#74e6db]">{card.bank}</p>
        <p className="font-display text-lg font-semibold leading-tight">{card.name}</p>
      </div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-white/85 border border-white/15 rounded-full px-2 py-0.5">
        <Sparkles className="h-3 w-3" /> Cashback
      </div>
    </div>

    <div className="flex items-center justify-between">
      <Chip tone="silver" />
      <Wifi className="h-4 w-4 text-white/60 rotate-90" />
    </div>

    <div>
      <p className="font-mono-num text-sm tracking-[0.18em] text-white/90">•••• •••• •••• {card.last4}</p>
      <div className="mt-2 flex items-end justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">Cardholder</p>
          <p className="text-sm text-white/95">Aarav Mehta</p>
        </div>
        <div className="font-display text-sm font-semibold text-white/95">{card.network}</div>
      </div>
    </div>
  </div>
);

// Variant 5: ICICI Emeralde — deep emerald + gold accent (Taj-luxury)
const EmeraldDesign = () => (
  <div className="absolute inset-0 metal-emerald emerald-edge">
    <div className="absolute inset-0 bg-fine-grain opacity-30 mix-blend-overlay" />
    <div className="absolute -top-12 -left-12 h-40 w-40 rounded-full bg-[radial-gradient(circle,rgba(50,200,140,0.18),transparent_70%)]" />
    <div className="absolute -bottom-12 -right-12 h-40 w-40 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.18),transparent_60%)]" />
  </div>
);
const EmeraldContent = ({ card }) => (
  <div className="relative flex h-full flex-col justify-between p-5 text-white">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#9ee5be]">{card.bank}</p>
        <EmbossedTitle>{card.name}</EmbossedTitle>
      </div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[rgba(255,220,140,0.95)] border border-[rgba(212,175,55,0.40)] rounded-full px-2 py-0.5">
        <Diamond className="h-3 w-3" /> Private
      </div>
    </div>

    <div className="flex items-center justify-between">
      <Chip tone="gold" />
      <Wifi className="h-4 w-4 text-white/60 rotate-90" />
    </div>

    <div>
      <p className="font-mono-num text-sm tracking-[0.18em] text-white/90">•••• •••• •••• {card.last4}</p>
      <div className="mt-2 flex items-end justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">Cardholder</p>
          <p className="text-sm text-white/95">Aarav Mehta</p>
        </div>
        <div className="font-display text-sm font-semibold text-white/95">{card.network}</div>
      </div>
    </div>
  </div>
);

const designMap = {
  'graphite-gold': { bg: InfiniaDesign, content: InfiniaContent },
  'navy-stars': { bg: AtlasDesign, content: AtlasContent },
  'silver-metallic': { bg: SilverDesign, content: SilverContent },
  'modern-teal': { bg: ModernDesign, content: ModernContent },
  'emerald-luxury': { bg: EmeraldDesign, content: EmeraldContent },
};

// ----------------------------------------------------------------
// PhotoVisual — when the card has a real, processed image (uploaded
// via the admin asset pipeline), we render the photo on a backdrop
// derived from the GPT-5 Vision colour tokens. This takes priority
// over the synthetic `design` variants below.
// ----------------------------------------------------------------
const PhotoVisual = ({ card }) => {
  const img = card?.image;
  const meta = img?.metadata || {};
  const primary = meta?.visual_tokens?.primary_color || '#0f1115';
  const accent  = meta?.visual_tokens?.accent_color  || 'rgba(255,255,255,0.18)';
  const url = `${BACKEND}/api/assets/cards/${card.id}/preview`;
  return (
    <>
      <div
        className="absolute inset-0"
        style={{
          background:
            `radial-gradient(120% 80% at 20% 0%, ${accent}40 0%, transparent 55%),` +
            `radial-gradient(120% 80% at 100% 100%, ${accent}30 0%, transparent 55%),` +
            primary,
        }}
      />
      <div className="absolute inset-0 bg-fine-grain opacity-30 mix-blend-overlay" />
      <div className="absolute inset-0 grid place-items-center p-4">
        <img
          src={url}
          alt={`${card.bank || ''} ${card.name || ''}`.trim() || 'Card'}
          className="max-h-full max-w-full object-contain drop-shadow-[0_18px_30px_rgba(0,0,0,0.55)]"
          draggable="false"
        />
      </div>
    </>
  );
};

export const CreditCardVisual = ({ card, className, size = 'md', onClick, selected = false }) => {
  // Phase 9: if the admin has uploaded a real image, render that on a
  // theme-aware backdrop. Otherwise fall back to the synthetic design
  // variants so cards without imagery still feel intentional.
  const hasImage = !!(card?.image && !card.image.is_deleted && card.image.urls?.preview);
  // Phase 12.4 — respect the vertical orientation flag captured at upload.
  const isVertical = !!(card?.image?.metadata?.features?.is_vertical);

  const variantKey = card.design && designMap[card.design] ? card.design : 'graphite-gold';
  const Variant = designMap[variantKey];
  const BgLayer = Variant.bg;
  const ContentLayer = Variant.content;

  return (
    <motion.button
      type="button"
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.985 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      onClick={onClick}
      data-testid={`credit-card-visual-${card.id}`}
      className={cn(
        'relative overflow-hidden rounded-2xl border text-left shadow-card focus-ring shine-on-hover',
        // Width vs. height behaviour depends on orientation:
        //   horizontal → width-driven (`w-full` + landscape aspect ratio)
        //   vertical   → height-driven (fixed `h-[…]` + portrait aspect,
        //                width auto-derives; centered in grid cell via mx-auto)
        isVertical
          ? cn('aspect-[1/1.586] w-auto mx-auto', verticalHeights[size] || verticalHeights.md)
          : cn('aspect-[1.586/1] w-full', sizes[size]),
        selected ? 'border-primary/50 ring-2 ring-primary/30' : 'border-white/10',
        // For horizontal cards `sizes[size]` already includes the max-width
        // and font-size tokens. For vertical cards we only need the font-size
        // part, which we forward through the same map (the `max-w-` rule is
        // ignored because we use `w-auto`).
        isVertical ? sizes[size]?.split(' ').filter((c) => c.startsWith('text-')).join(' ') : null,
        className
      )}
    >
      {hasImage ? <PhotoVisual card={card} /> : <BgLayer card={card} />}
      {!hasImage && <ContentLayer card={card} />}
    </motion.button>
  );
};
